import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform, useWindowDimensions, type TextStyle } from 'react-native';
import type { ThemeColors } from '../../../../shared/theme';
import { appTheme } from '../../../../shared/theme';
import { DASHBOARD_PADDING_H, DASH_STUDIO_INNER } from '../../../../shared/theme/studio/dashboardConstants';
import { useTranslation } from '../../../../shared/i18n';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { Clickable } from '../../../../components/common/Clickable';
import { HorizontalLtrScrollView } from '../../../../components/ui/HorizontalLtrScrollView';
import { DashboardCardShellSet2 } from './DashboardCardShellSet2';
import {
  MenuImageTile,
  computeMenuImageTileMetrics,
  MENU_TILE_STRIP_GAP,
  type FlatItem,
  type MenuImageTileMetrics,
} from '../../DashboardMenuImagesStrip';

const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

export interface DashboardSelectedMenuCardSet2Props {
  colors: ThemeColors;
  items: FlatItem[];
  title: string;
  on_press?: () => void;
  tile_metrics?: MenuImageTileMetrics;
}

/** v2 dashboard — selected meals strip (title + view all, image tiles). */
export function DashboardSelectedMenuCardSet2({
  colors,
  items,
  title,
  on_press,
  tile_metrics,
}: DashboardSelectedMenuCardSet2Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const text_align = rtl.textAlign;
  const writing_direction = rtl.writingDirection;
  const { width: screen_width } = useWindowDimensions();
  const metrics = tile_metrics ?? computeMenuImageTileMetrics(screen_width, MENU_TILE_STRIP_GAP);
  const card_padding = DASH_STUDIO_INNER;

  const needs_scroll = useMemo(() => {
    const strip_width = screen_width - DASHBOARD_PADDING_H * 2 - card_padding * 2;
    const content_width = items.length * metrics.tile_width + Math.max(0, items.length - 1) * MENU_TILE_STRIP_GAP;
    return content_width > strip_width;
  }, [screen_width, items.length, metrics.tile_width, card_padding]);

  const max_height = metrics.tile_height + card_padding * 2 + 12;

  return (
    <DashboardCardShellSet2 colors={colors} no_padding>
      {title !== '' ? (
        <View style={[styles.titleWrap, { paddingHorizontal: card_padding, paddingTop: card_padding, paddingBottom: appTheme.spacing.xs }]}>
          {on_press != null ? (
            <Clickable onPress={on_press} accessibilityLabel={title} accessibilityRole="button">
              <View style={styles.titleRow}>
                <Text
                  style={[styles.title, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
                  numberOfLines={1}>
                  {title}
                </Text>
                <Text style={[styles.titleLink, { color: colors.primary }, ANDROID_TEXT]}>{t('dashboard.viewAll')}</Text>
              </View>
            </Clickable>
          ) : (
            <Text
              style={[styles.title, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
              numberOfLines={1}>
              {title}
            </Text>
          )}
        </View>
      ) : null}
      <HorizontalLtrScrollView
        style={[styles.scroll, { maxHeight: max_height }]}
        scrollEnabled={needs_scroll}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingVertical: appTheme.spacing.md,
            paddingHorizontal: card_padding,
            paddingBottom: card_padding,
            alignItems: 'flex-start',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            gap: MENU_TILE_STRIP_GAP,
            ...(needs_scroll ? null : { flexGrow: 1, justifyContent: 'flex-start' }),
          },
        ]}
        showsHorizontalScrollIndicator={false}
        nestedScrollEnabled
        horizontal>
        {items.map(item => (
          <MenuImageTile key={item.key} item={item} colors={colors} tile_metrics={metrics} />
        ))}
      </HorizontalLtrScrollView>
    </DashboardCardShellSet2>
  );
}

const styles = StyleSheet.create({
  titleWrap: {},
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    minHeight: 20,
  },
  title: {
    ...appTheme.typography.captionMini,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    flex: 1,
    minWidth: 0,
    fontWeight: '800',
    fontSize: 10,
  },
  titleLink: {
    fontSize: 12,
    fontWeight: '700',
    flexShrink: 0,
  },
  scroll: {
    margin: 0,
    padding: 0,
  },
  scrollContent: {},
});
